import { DateTime } from 'luxon'
import { isEmpty, isNumber, sum, take, uniq } from 'lodash-es'

const Privileges = {
    ffPlanEligible: 'ffPlanEligible',
    unlimitedTokens: 'unlimitedTokens',
    // voiceChat: 'voiceChat',
    allowedFiveRels: 'allowedFiveRels',
    manageOrgs: 'manageOrgs',
    manageUsers: 'manageUsers',
    manageSettings: 'manageSettings',
    diagnostics: 'diagnostics',
}

const Groups = {
    admin: 'admin',
}

const GlobalUserPrivs = (process.env.KN_GLOBAL_USER_PRIVS || '').split(',')

const PrivilegesByGroup = {
    [Groups.admin]: Object.keys(Privileges),
}

function getGroupPrivileges({ groups }) {
    const privs = []
    for (let e of (groups || [])) privs.push(...(PrivilegesByGroup[e] || []))
    return uniq(privs)
}

async function userHasPrivAsync({ privilege, privileges, getPrivsFn }) {
    if (GlobalUserPrivs.includes(privilege)) return true
    const privs = privileges || await getPrivsFn()
    return privs.includes(privilege)
}

function userHasPriv({ privilege, privileges }) {
    if (GlobalUserPrivs.includes(privilege)) return true
    return privileges.includes(privilege)
}

function canManageOrgs({ privileges }) {
    return userHasPriv({ privilege: Privileges.manageOrgs, privileges })
}

function canManageUsers({ privileges }) {
    return userHasPriv({ privilege: Privileges.manageUsers, privileges })
}

function canManageSettings({ privileges }) {
    return userHasPriv({ privilege: Privileges.manageSettings, privileges })
}

const canManageOrgId = async ({ privileges, orgId }) =>
    await canManageOrgs({ privileges }) ||
    await userHasPriv({ privilege: `manageOrgId:${orgId}`, privileges })

const memberOfOrgId = async ({ privileges, orgId }) =>
    await canManageOrgId({ privileges }) ||
    await userHasPriv({ privilege: `memberOfOrgId:${orgId}`, privileges })

/**
 * @param {number} length
 * @returns {string}
 */
function randomString(length) {
    return Array(length)
        .fill('')
        .map(() => '0123456789abcdefghijklmnopqrstuvwxyz' [Math.floor(Math.random() * 36)])
        .join('')
}

/**
 * example: thread__2024-10-23T02:26:45.479Z__abcd0000
 * @param {string} itemType
 * @param {string} created
 */
function createItemId({ itemType, created = DateTime.utc().toString() }) {
    return [itemType, created, randomString(8)].join('__')
}

function createUserId() {
    return 'user__' + randomString(10)
}

function createOrgId() {
    return 'org__' + randomString(10)
}

function isHelpMessage({ content }) {
    return (content || '').startsWith('help:') && Object.keys(HelpItems).includes(content.split(':')[1])
}

function getHelpLabel({ help }) {
    const k = help.split(':')[1]
    return HelpItems[k].label
}

const HelpItems = {
    coreFeelingsNeeds: { placeholder: 'help:coreFeelingsNeeds', label: 'Core Feelings & Needs Summary' },
    connectedMessage: { placeholder: 'help:connectedMessage', label: 'Connected Message' },
    encouragingSelfTalk: { placeholder: 'help:encouragingSelfTalk', label: 'Encouraging Self-Talk' },
    conversationStarters: { placeholder: 'help:conversationStarters', label: 'Conversation Starters' },
    strategiesSolutionsDiscussed: {
        placeholder: 'help:strategiesSolutionsDiscussed',
        label: 'Strategies & Solutions Discussed',
    },
    tryRolePlaying: { placeholder: 'help:tryRolePlaying', label: 'Try Role Playing' },
    summarize: { placeholder: 'help:summarize', label: 'Summarize' },
}

const UserPreferenceLabels = {
    empathetic: 'I prefer a warm and empathetic approach.',
    direct: 'I prefer a direct and straightforward approach.',
    support: 'I prefer focusing on emotional support and understanding.',
    solutions: 'I prefer focusing on practical solutions and strategies.',
    gentle: 'I prefer gentle encouragement to build confidence.',
    challenge: 'I like to be challenged to step out of my comfort zone.',
    slow: 'I prefer taking things slow and reflecting deeply.',
    fast: 'I like a faster pace with actionable steps.',
    detailed: 'I prefer detailed, explanatory feedback.',
    brief: 'I like brief, concise feedback to keep things simple.',
}

async function sleep(millis) {
    await new Promise((resolve) => setTimeout(resolve, millis))
}

function isRelationshipMessage({ content }) {
    return (content || '').startsWith('relationshipId:')
}

function getRelationshipId({ content }) {
    return content.split(':')[1]
}

/**
 * example loveRx data: { 'gpr:pt:1': 5, 'whv:as:10': 4, ... }
 */
function calculateLoveRxScores({ loveRx }) {
    const contextMap = {}
    const situationalMap = {}

    // Step 1: Aggregate scores and count for each context:expression
    for (const [key, score] of Object.entries(loveRx)) {
        const [context, expression] = key.split(':')

        if (!contextMap[context]) contextMap[context] = {}
        if (!contextMap[context][expression]) contextMap[context][expression] = { total: 0, count: 0 }

        contextMap[context][expression].total += score
        contextMap[context][expression].count += 1

        // Add to situational map if context is not "gpr"
        if (context !== 'gpr') {
            if (!situationalMap[expression]) situationalMap[expression] = { total: 0, count: 0 }
            situationalMap[expression].total += score
            situationalMap[expression].count += 1
        }
    }

    // Step 2: Compute averages and sort expressions by average score
    const results = {}
    for (const [context, expressions] of Object.entries(contextMap)) {
        // Sort by average score descending
        results[context] = Object.entries(expressions)
            .map(([expression, data]) => {
                const average = data.count > 0 ? data.total / data.count : 0 // Avoid division by zero
                return { [expression]: average }
            })
            .sort((a, b) => Object.values(b)[0] - Object.values(a)[0])
    }

    // Step 3: Compute situational context, sort by average score descending
    results['situational'] = Object.entries(situationalMap)
        .map(([expression, data]) => {
            const average = data.count > 0 ? data.total / data.count : 0 // Avoid division by zero
            return { [expression]: average }
        })
        .sort((a, b) => Object.values(b)[0] - Object.values(a)[0])

    const generalResults = []
    for (let e of take(results.gpr, 3)) {
        generalResults.push(LoveRxExpressionTitles[Object.keys(e)[0]])
    }
    const situationalResults = {}
    for (let context of Object.keys(results).filter(e => !['gpr', 'situational'].includes(e))) {
        const k = LoveRxContextTitles[context]
        if (!situationalResults[k]) situationalResults[k] = []
        situationalResults[k].push(LoveRxExpressionTitles[Object.keys(results[context][0])[0]])
        situationalResults[k].push(LoveRxExpressionTitles[Object.keys(results[context][1])[0]])
        situationalResults[k].push(LoveRxExpressionTitles[Object.keys(results[context][2])[0]])
    }
    return { gpr: generalResults, ...situationalResults }
}

function calculateConflictStyleScores({ conflictStyle }) {
    const styleScores = {}
    for (const [key, value] of Object.entries(conflictStyle)) {
        const [style] = key.split(':')
        styleScores[style] = (styleScores[style] || 0) + value
    }
    return take(
        Object.entries(styleScores)
            .map(([style, totalScore]) => ({ [style]: totalScore }))
            .sort((a, b) => Object.values(b)[0] - Object.values(a)[0])
            .map(e => ConflictStyleTitles[Object.keys(e)[0]]),
        2)
}

// individual scores
function calculateAttachmentStyleScores({ attachmentStyle, questionCounts = { xa: 18, va: 18 } }) {
    const { anxietyLevel, avoidanceLevel } = _calculateAttachmentStyleScores(attachmentStyle, questionCounts)
    const score = `${anxietyLevel}-${avoidanceLevel}`
    const result = {}
    switch (score) {
        case 'Low-Low':
            result.style = `${anxietyLevel} Anxiety, ${avoidanceLevel} Avoidance (Secure Attachment)`
            result.description = 'You are securely attached, which means you feel confident in both giving and receiving emotional support. Relationships come naturally to you, and you view them as safe spaces where trust and vulnerability are rewarded rather than risky. You aren\'t preoccupied with fears of rejection or abandonment, nor do you feel the need to withdraw to maintain your independence. This balance makes you a reliable and nurturing presence in others\' lives. <br/><br/> You likely find it easy to communicate openly and resolve conflicts constructively, believing that disagreements are opportunities for growth rather than threats. You trust that others will respond to your needs and that they care for you without requiring constant proof. This security allows you to take risks, explore deeper emotional connections, and build relationships that feel mutually rewarding and stable. <br/><br/> In difficult situations, you are resilient. You seek help when needed and offer it when asked. Others may describe you as approachable, understanding, and emotionally intelligent. To explore how your secure attachment style contributes to your strengths and opportunities for growth, talk to Amari. Together, you can delve deeper into how this foundation supports your relationships and self-development.'
            break
        case 'High-Low':
            result.style = `${anxietyLevel} Anxiety, ${avoidanceLevel} Avoidance (Preoccupied/Anxious Attachment)`
            result.description = 'You have a preoccupied attachment style, meaning that you deeply desire emotional closeness but often fear it won\'t last. You might frequently worry that others don\'t value you as much as you value them or that they might leave or reject you. This fear often manifests as seeking constant reassurance, analyzing interactions for hidden meanings, or striving to prove your worth in relationships. <br/><br/> While you are comfortable with emotional intimacy, your heightened anxiety about rejection can create tension. You may feel compelled to check in excessively with friends, partners, or colleagues, asking questions like, “Do you still care about me?” or “Did I do something wrong?” This need for validation can sometimes overwhelm others, leaving you feeling misunderstood or unsupported.<br/><br/> At the same time, your ability to care deeply and connect emotionally is a tremendous strength. You notice the small details and invest in your relationships with genuine intention. With Amari, you can learn how to channel these strengths while managing your fears of rejection. Together, you can explore ways to build self-assurance, trust your relationships, and reduce the anxiety that weighs on your connections.'
            break
        case 'Low-High':
            result.style = `${anxietyLevel} Anxiety, ${avoidanceLevel} Avoidance (Dismissive Attachment)`
            result.description = 'You have a dismissive attachment style, valuing independence and self-reliance over emotional closeness. You may downplay the importance of relationships and feel uncomfortable with vulnerability. Depending on others—or allowing them to depend on you—might feel like a burden or risk you prefer to avoid.<br/><br/> You likely see yourself as confident and self-sufficient, which can be a strength in many situations. However, this independence can sometimes result in emotional distance or difficulty forming deep, meaningful relationships. You might keep conversations surface-level, avoid discussing personal struggles, or brush off attempts by others to get closer to you. People in your life may view you as aloof or uninvested, even if you care deeply but struggle to express it.<br/><br/>Your hesitance to engage in emotional intimacy often stems from a desire to protect yourself. By avoiding closeness, you reduce the risk of rejection, disappointment, or feeling out of control. Amari can help you explore why emotional vulnerability feels challenging and how you can take small steps toward building trust and connection without sacrificing your independence.'
            break
        default:
            result.style = `${anxietyLevel} Anxiety, ${avoidanceLevel} Avoidance (Fearful Attachment)`
            result.description = 'You have a fearful attachment style, marked by a deep inner conflict. You long for connection and emotional intimacy but feel deeply afraid of vulnerability and rejection. This creates a push-pull dynamic where you might seek closeness one moment but withdraw the next, leaving you and those around you feeling uncertain about where you stand.<br/><br/>This style often stems from past experiences of hurt or rejection that make it difficult to trust others—or even yourself—in relationships. You may hold back from expressing your needs or emotions, fearing they’ll be misunderstood or dismissed. At the same time, when you sense others pulling away, your anxiety may spike, leading to behaviors that attempt to regain closeness or control.<br/><br/>This attachment style can feel exhausting, as you wrestle with opposing desires for intimacy and self-protection. Amari can help you unpack this internal struggle, offering insights into how your fears and longings shape your behavior. Together, you can work on strategies to build trust and find balance, creating safer, more fulfilling connections.'
    }
    return result
}

// relationship scores
function calculateRelAttachmentStyleScores({ attachmentStyle, questionCounts = { xa: 3, va: 6 } }) {
    const { anxietyLevel, avoidanceLevel } = _calculateAttachmentStyleScores(attachmentStyle, questionCounts)
    const score = `${anxietyLevel}-${avoidanceLevel}`
    const result = {}
    switch (score) {
        case 'Low-Low':
            result.style = `${anxietyLevel} Anxiety, ${avoidanceLevel} Avoidance (Secure Attachment)`
            result.description = 'In this relationship, you feel at ease with emotional closeness and trust. You are confident in the bond you share with this individual and feel secure in both giving and receiving support. You\'re not preoccupied with fears of rejection or abandonment, and you trust this person\'s intentions and care for you. When conflicts arise, you address them constructively, seeking resolution without feeling overwhelmed or threatened.<br/><br/>This secure foundation allows you to experience the relationship as a source of strength and stability. You likely feel comfortable expressing your needs and emotions openly, and you trust that this individual values you for who you are. This trust creates a safe environment where both of you can grow and deepen your connection.<br/><br/>To continue nurturing this security, focus on maintaining open communication and reinforcing the trust you\'ve built. If challenges arise, remind yourself of the resilience and stability already present in this relationship. You can also reflect with Amari on how your secure attachment supports this connection and explore ways to share this strength with others in your life.'
            break
        case 'High-Low':
            result.style = `${anxietyLevel} Anxiety, ${avoidanceLevel} Avoidance (Preoccupied/Anxious Attachment)`
            result.description = 'With this individual, you deeply value emotional closeness and connection, but you may also feel a heightened fear of rejection or abandonment. You might find yourself seeking frequent reassurance or overanalyzing their words and actions, worried that they might not care for you as much as you care for them. This need for validation stems from your strong desire to feel secure in the relationship, but it can sometimes leave you feeling drained or unfulfilled.<br/><br/>Despite these worries, your commitment to the relationship is a strength. You care deeply and are willing to invest time and energy into making it work. However, these fears of being let down or left behind can make it challenging to fully enjoy the connection.<br/><br/>To foster more balance in this relationship, consider small steps toward self-assurance. Reflect on the times when this individual has shown care and loyalty, and remind yourself that you are worthy of love without needing constant validation. Amari can help you explore how to calm these fears and communicate your needs in a way that strengthens the relationship.'
            break
        case 'Low-High':
            result.style = `${anxietyLevel} Anxiety, ${avoidanceLevel} Avoidance (Dismissive Attachment)`
            result.description = 'In this relationship, you may feel more comfortable maintaining emotional distance than pursuing deep intimacy. You likely value your independence and self-sufficiency, which can make it challenging for you to rely on this individual or share your vulnerabilities. While you may not fear rejection or abandonment, you might hesitate to express your emotions fully, believing that doing so could compromise your autonomy.<br/><br/>This tendency to avoid emotional closeness can sometimes leave the other person feeling disconnected or unsure of where they stand with you. While you may see your independence as a strength—and it often is—it\'s important to recognize that emotional vulnerability is not a weakness but a pathway to deeper connection.<br/><br/>To strengthen this relationship, try taking small, intentional steps toward openness. Share a thought or feeling you\'ve been holding back, or allow this individual to support you in a way that feels safe. Amari can guide you through this process, helping you find a balance between independence and connection in this relationship.'
            break
        default:
            result.style = `${anxietyLevel} Anxiety, ${avoidanceLevel} Avoidance (Fearful Attachment)`
            result.description = 'With this individual, you may feel torn between your desire for closeness and your fear of vulnerability. You likely long for a deep connection, but at the same time, you may hesitate to trust this person fully or fear getting hurt. This internal conflict can create a push-pull dynamic in the relationship, where you seek closeness one moment but withdraw the next.<br/><br/>This pattern might leave you feeling uncertain about where you stand or how to navigate the relationship. While it’s natural to want to protect yourself from pain, this fear can prevent you from experiencing the full potential of the bond you share with this person.<br/><br/>Amari can help you untangle these conflicting emotions, offering tools to address both your fears and your longing for connection. Together, you can explore ways to build trust—both in yourself and in this individual—so you can create a relationship that feels safe, consistent, and fulfilling.'
    }
    return result
}

function _calculateAttachmentStyleScores(attachmentStyle, questionCounts) {
    const styleScores = {}
    for (const [key, value] of Object.entries(attachmentStyle)) {
        const [style] = key.split(':')
        styleScores[style] = (styleScores[style] || 0) + value
    }
    const averages = {}
    for (let style of Object.keys(styleScores)) {
        const totalScore = styleScores[style]
        averages[style] = questionCounts[style] ? totalScore / questionCounts[style] : 0
    }
    const anxietyLevel = averages['xa'] <= 2.9 ? 'Low' : averages['xa'] <= 2.9 ? 'Moderate' : 'High'
    const avoidanceLevel = averages['va'] <= 2.9 ? 'Low' : averages['va'] <= 2.9 ? 'Moderate' : 'High'
    return { anxietyLevel, avoidanceLevel }
}

const wbJeLabels = ['Strongly Disagree', 'Disagree', 'Slightly Disagree', 'Slightly Agree', 'Agree', 'Strongly Agree']
const dsJeLabels = ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree']
const WellBeingCategory_Je = {
    questionsLength: 2,
    idPrefix: 'wb:',
    title: 'How is your well-being?',
    maxValue: 5,
    labels: wbJeLabels,
}
const DistressCategory_Je = {
    questionsLength: 1,
    idPrefix: 'ds:',
    title: 'Are you feeling distress?',
    maxValue: 5,
    labels: dsJeLabels,
}

const wbBlLabels = ['Never', 'Once or Twice', 'About Once a Week', '2 or 3 Times a Week', 'Almost Every Day', 'Every Day']
const dsBlLabels = [
    'Not at all',
    'Some of the time',
    'A good part of the time',
    'Most of the time',
]
const WellBeingCategory_Bl = {
    questionsLength: 14,
    idPrefix: 'wb:',
    title: 'In the past month,',
    maxValue: 5,
    labels: wbBlLabels,
}
const DistressCategory_Bl = {
    questionsLength: 21,
    idPrefix: 'ds:',
    title: 'In the past month, how much did this apply to you?',
    maxValue: 3,
    labels: dsBlLabels,
}

function getWellBeingScore({ scores, category = WellBeingCategory_Je }) {
    return Math.round(70 * _getScore({ scores, category }))
}

function getDistressScore({ scores, category = DistressCategory_Je }) {
    return Math.round(126 * _getScore({ scores, category }))
}

function _getScore({ scores, category }) {
    const total = sum(
        Object.entries(scores || {})
            .filter(([k]) => k.startsWith(category.idPrefix))
            .map(([, v]) => v),
    )
    return total / (category.maxValue * category.questionsLength)
}

function getKinectinScoreQuadrantLabel({
                                           scores,
                                           wbCategory = WellBeingCategory_Je,
                                           dsCategory = DistressCategory_Je,
                                       }) {
    if (isEmpty(scores)) return null
    const wbScore = getWellBeingScore({ scores, category: wbCategory }) / 70
    const dsScore = getDistressScore({ scores, category: dsCategory }) / 126
    if (wbScore < 0.5 && dsScore < 0.5) return 'Languishing'
    if (wbScore >= 0.5 && dsScore < 0.5) return 'Flourishing'
    if (wbScore >= 0.5 && dsScore >= 0.5) return 'Functional Distress'
    if (wbScore < 0.5 && dsScore >= 0.5) return 'Elevated Distress'
    throw Error('bad scores')
}

function getKinectinScoreQuadrantLabelData({ wellbeingScore, distressScore }) {
    if (!isNumber(wellbeingScore) || !isNumber(distressScore)) return { color: '#dbdaf7', title: 'No data available' }
    const wbScore = wellbeingScore / 70
    const dsScore = distressScore / 126
    const score = wbScore - dsScore
    if (wbScore < 0.5 && dsScore >= 0.5) return { color: '#fe6b6c', title: 'Elevated Distress', score }
    if (wbScore < 0.5 && dsScore < 0.5) return { color: '#2d8bba', title: 'Languishing', score: 10 + score }
    if (wbScore >= 0.5 && dsScore >= 0.5) return { color: '#fed000', title: 'Functional Distress', score: 100 + score }
    if (wbScore >= 0.5 && dsScore < 0.5) return { color: '#11bb9a', title: 'Flourishing', score: 1000 + score }
    return { color: '#dbdaf7', title: 'No data available' }
}

const QuadrantToColorMap = {
    'Elevated Distress': '#fe6b6c',
    'Languishing': '#2d8bba',
    'Functional Distress': '#fed000',
    'Flourishing': '#11bb9a',
    'No data available': '#dbdaf7',
}

const ConflictStyleTitles = {
    ca: 'Conflict-Avoidant',
    vo: 'Volatile',
    va: 'Validating',
    ho: 'Hostile',
    hd: 'Hostile-Detached',
}

const LoveRxContextTitles = {
    gpr: 'General Preferences',
    whv: 'When I Feel Overwhelmed',
    drc: 'During Celebrations',
    prt: 'When Physically Apart',
    dcf: 'During Conflict',
    mtr: 'During Major Transitions',
}

const LoveRxExpressionTitles = {
    pt: 'Physical Touch',
    wa: 'Words of Affirmation',
    sg: 'Supportive Growth',
    gf: 'Gifts',
    pl: 'Playfulness',
    qt: 'Quality Time',
    as: 'Acts of Service',
    si: 'Social Inclusion',
    rv: 'Responsiveness',
}


function getOrgPublicJsonPath({ urlSegment }) {
    return `org_content/${urlSegment}.json`
}

function isValidEmail(v, pattern) {
    return new RegExp(pattern || `.+@.+\\..+`).test(v)
}

const ReportTypes = {
    UserDetailReport: 'UserDetailReport',
    UserSummaryReport: 'UserSummaryReport',
}

const BestPossibleSelfActivityId = 'activity__2025-08-29T00:00:00.000Z__knact001'

const KinectinOrgId = process.env.KN_KINECTIN_ORG_ID

const ActivityTags = {
    'Hope': {},
    'Gratitude': {},
    'Positive Boost': {},
    'Resilience': {},
    'Achievement': {},
    'Connection': {},
}

const baseSpaUrl =
    (process.env.KN_SPA_ENDPOINT || `https://${process.env.KN_ENV}.kinectin.com`) + '/signinwith'

function getActivityUrl(activity, org) {
    const activityName = activity.name.replace(/\s+/g, '-').replace(/[^A-Za-z0-9-]/g, '').toLowerCase()
    return baseSpaUrl + `/${org.urlSegment}` +
        `?redirect=${encodeURIComponent(`/activity?orgId=${org.orgId}&activityId=${activity.activityId}`)}` +
        `&activityName=${encodeURIComponent(activityName)}`
}

// Daily Journal scoring functions
function getDailyJournalScore({ scores }) {
    const entries = Object.entries(scores || {})
        .filter(([qid]) => {
            // since the refactor on 2025-09-27, these should all be wb:100+, ds:100+
            const qidNum = qid.split(':')[1]
            return Number(qidNum) >= 100
        })
    if (isEmpty(entries)) return null

    const maxValue = 5
    let sum = 0
    for (let [qid, value] of entries) {
        const qidType = qid.split(':')[0]
        value += 1 // make it 1-based
        // For distress questions (ds), invert the score (lower is better)
        // For wellbeing questions (wb) higher is better
        sum += qidType === 'ds' ? maxValue - value : value
    }
    return sum / entries.length
}

function getDailyJournalLabel({ score }) {
    if (score === null || score === undefined) return 'No data available'
    if (score >= 4.5) return 'Great'
    if (score >= 3.5) return 'Good'
    if (score >= 2.5) return 'Okay'
    if (score >= 1.5) return 'Rough'
    return 'Difficult'
}

function getDailyJournalEmoji({ score }) {
    if (score === null || score === undefined) return 'unknown-face.png'
    if (score >= 4.5) return 'big-smile.png'
    if (score >= 3.5) return 'happy-face.png'
    if (score >= 2.5) return 'neutral-face.png'
    if (score >= 1.5) return 'frown-face.png'
    return 'teary-face.png'
}

export {
    getActivityUrl,
    ActivityTags,
    KinectinOrgId,
    BestPossibleSelfActivityId,
    randomString,
    createItemId,
    createUserId,
    createOrgId,
    HelpItems,
    isHelpMessage,
    getHelpLabel,
    UserPreferenceLabels,
    sleep,
    isRelationshipMessage,
    getRelationshipId,
    calculateLoveRxScores,
    calculateConflictStyleScores,
    calculateAttachmentStyleScores,
    calculateRelAttachmentStyleScores,
    LoveRxContextTitles,
    Privileges,
    Groups,
    getGroupPrivileges,
    GlobalUserPrivs,
    getOrgPublicJsonPath,
    isValidEmail,
    userHasPriv,
    userHasPrivAsync,
    canManageUsers,
    canManageSettings,
    canManageOrgs,
    canManageOrgId,
    memberOfOrgId,
    ReportTypes,
    WellBeingCategory_Je,
    DistressCategory_Je,
    WellBeingCategory_Bl,
    DistressCategory_Bl,
    getWellBeingScore,
    getDistressScore,
    getKinectinScoreQuadrantLabel,
    getKinectinScoreQuadrantLabelData,
    QuadrantToColorMap,
    getDailyJournalScore,
    getDailyJournalLabel,
    getDailyJournalEmoji,
}